import React, { useMemo, useState } from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EnvioSectionBand from "./EnvioSectionBand";
import {
  buildLogisticaDocChecklist,
  fileKind,
  logisticaDocDisplayName,
  logisticaDocPublicUrl,
} from "./logisticaUtils";

const EnvioDocumentosChips = ({
  documentos,
  onMissingClick,
  compact = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const checklist = useMemo(
    () => buildLogisticaDocChecklist(documentos),
    [documentos],
  );
  const presentCount = checklist.filter((item) => item.present).length;
  const [preview, setPreview] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(0);

  const stop = (event) => {
    event.stopPropagation();
  };

  const docs = preview?.item?.docs || [];
  const selectedDoc = docs[selectedIdx] || docs[0] || null;
  const selectedUrl = selectedDoc
    ? logisticaDocPublicUrl(selectedDoc.Archivo)
    : "";
  const selectedName = selectedDoc ? logisticaDocDisplayName(selectedDoc) : "";
  const selectedKind = fileKind(selectedName || selectedDoc?.Archivo);

  const openPreview = (item) => {
    setSelectedIdx(0);
    setPreview({ item });
  };

  const countLabel = t("logisticaPedidos.docsCount", {
    ok: presentCount,
    total: checklist.length,
  });
  const countColor =
    presentCount === checklist.length
      ? theme.palette.success.main
      : presentCount === 0
        ? theme.palette.warning.main
        : theme.palette.text.secondary;

  const chips = (
    <Stack
      direction="row"
      spacing={1.1}
      useFlexGap
      flexWrap="wrap"
      alignItems="center"
      sx={compact ? { display: "contents" } : { rowGap: 1.1 }}
    >
      {compact ? (
        <Chip
          size="small"
          label={countLabel}
          sx={{
            fontWeight: 700,
            backgroundColor: alpha(
              presentCount === checklist.length
                ? theme.palette.success.main
                : theme.palette.info.main,
              0.1,
            ),
            color:
              presentCount === checklist.length
                ? theme.palette.success.main
                : theme.palette.info.main,
          }}
        />
      ) : null}
      {checklist.map((item) => {
        const ok = item.present;
        const color = ok
          ? theme.palette.success.main
          : theme.palette.text.secondary;
        const label =
          ok && item.docs.length > 1
            ? `${item.short} · ${item.docs.length}`
            : item.short;
        return (
          <Tooltip
            key={item.id}
            title={
              ok
                ? t("logisticaPedidos.docsPresentHint", { tipo: item.tipo })
                : t("logisticaPedidos.docsMissingHint", { tipo: item.tipo })
            }
          >
            <Chip
              size="small"
              label={label}
              onClick={(event) => {
                stop(event);
                if (ok) {
                  openPreview(item);
                  return;
                }
                onMissingClick?.();
              }}
              sx={{
                fontWeight: 700,
                fontSize: compact ? "0.72rem" : "0.7rem",
                backgroundColor: alpha(color, ok ? 0.14 : 0.06),
                color,
                border: "1px solid",
                borderColor: alpha(color, ok ? 0.32 : 0.18),
                opacity: ok ? 1 : 0.72,
              }}
            />
          </Tooltip>
        );
      })}
    </Stack>
  );

  const previewDialog = (
    <Dialog
      open={Boolean(preview)}
      onClose={() => setPreview(null)}
      maxWidth={false}
      onClick={stop}
      PaperProps={{
        sx: {
          width: "min(96vw, 1200px)",
          height: "min(92vh, 920px)",
          maxWidth: "96vw",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          py: 1.1,
          pr: 1,
        }}
      >
        <Typography component="span" fontWeight={800} noWrap sx={{ flex: 1 }}>
          {preview?.item?.tipo || t("logisticaPedidos.docsStrip")}
        </Typography>
        {selectedUrl ? (
          <Tooltip title={t("logisticaPedidos.docsOpenNew")}>
            <IconButton
              size="small"
              component="a"
              href={selectedUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={stop}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
        <IconButton
          size="small"
          onClick={() => setPreview(null)}
          aria-label={t("logisticaPedidos.docsClose")}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          gap: 1.25,
          pt: "8px !important",
          pb: 1.5,
          flex: 1,
          minHeight: 0,
        }}
      >
        {docs.length > 1 ? (
          <Stack spacing={0.75} sx={{ width: 180, flexShrink: 0 }}>
            {docs.map((doc, idx) => {
              const name = logisticaDocDisplayName(doc);
              return (
                <Chip
                  key={doc.id_Documento || name}
                  size="small"
                  label={name || doc.TipoDocumento}
                  onClick={() => setSelectedIdx(idx)}
                  sx={{
                    justifyContent: "flex-start",
                    maxWidth: "100%",
                    fontWeight: idx === selectedIdx ? 800 : 600,
                    backgroundColor: alpha(
                      theme.palette.primary.main,
                      idx === selectedIdx ? 0.16 : 0.06,
                    ),
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                />
              );
            })}
          </Stack>
        ) : null}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1.5,
            overflow: "hidden",
            bgcolor: "background.default",
          }}
        >
          {selectedUrl && selectedKind === "image" ? (
            <Box
              component="img"
              src={selectedUrl}
              alt={selectedName}
              sx={{
                display: "block",
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          ) : selectedUrl && selectedKind === "pdf" ? (
            <Box
              component="iframe"
              src={`${selectedUrl}#toolbar=1&navpanes=0`}
              title={selectedName || preview?.item?.tipo || "PDF"}
              sx={{
                width: "100%",
                height: "100%",
                border: "none",
                bgcolor: "background.paper",
              }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              {t("logisticaPedidos.docsNoPreview")}
            </Typography>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );

  if (compact) {
    return (
      <Box onClick={stop} onMouseDown={stop} sx={{ display: "contents" }}>
        {chips}
        {previewDialog}
      </Box>
    );
  }

  return (
    <EnvioSectionBand
      title={t("logisticaPedidos.docsStrip")}
      count={countLabel}
      countColor={countColor}
      tone="docs"
      onClick={stop}
      onMouseDown={stop}
    >
      {chips}
      {previewDialog}
    </EnvioSectionBand>
  );
};

export default EnvioDocumentosChips;
